import { ARC_STATUS } from './arcVisualSystem';
import type { StrokeSample } from '../../../features/dashboards/rowingArcTypes';

export type MetricType = 'catch' | 'finish' | 'gap' | 'slip';
export type MarkerStatus = 'good' | 'caution' | 'bad' | 'na';

export interface ArcTargets {
  catchAngleMin: number;
  catchAngleMax: number;
  finishAngleMin: number;
  finishAngleMax: number;
  catchGapMax: number;
  finishSlipMin: number;
  finishSlipMax: number;
  warningBand: number;
}

export interface MetricDetails {
  label: string;
  value: number;
  target: string;
  min?: number;
  max?: number;
}

export interface MetricTargetRange {
  min?: number;
  max?: number;
  label: string;
}

export interface SessionAverages {
  catchGap: number | null;
  effectiveLength: number | null;
  finishSlip: number | null;
  measuredCatchGapCount: number;
  totalStrokeCount: number;
}

export const TARGET_STORAGE_KEY = 'rowing-arc-targets';

export const TARGETS_STORAGE_VERSION = 2;

/** Product defaults before catch widening and slip ceiling-only scoring. */
export const LEGACY_DEFAULT_TARGETS: ArcTargets = {
  catchAngleMin: 56.0,
  catchAngleMax: 58.0,
  finishAngleMin: 30.0,
  finishAngleMax: 34.0,
  catchGapMax: 8.0,
  finishSlipMin: 3.0,
  finishSlipMax: 7.0,
  warningBand: 0.05,
};

export const DEFAULT_TARGETS: ArcTargets = {
  catchAngleMin: 55.0,
  catchAngleMax: 59.0,
  finishAngleMin: 30.0,
  finishAngleMax: 34.0,
  catchGapMax: 8.0,
  finishSlipMin: 0.0,
  finishSlipMax: 7.0,
  warningBand: 0.05,
};

export interface StoredArcTargetsV2 {
  version: number;
  overrides: Partial<ArcTargets>;
}

const ARC_TARGET_FIELDS: (keyof ArcTargets)[] = [
  'catchAngleMin',
  'catchAngleMax',
  'finishAngleMin',
  'finishAngleMax',
  'catchGapMax',
  'finishSlipMin',
  'finishSlipMax',
  'warningBand',
];

const targetValuesEqual = (left: number, right: number) => Math.abs(left - right) < 0.0001;

const isArcTargetsShape = (value: unknown): value is ArcTargets =>
  typeof value === 'object' &&
  value !== null &&
  ARC_TARGET_FIELDS.every(
    (field) =>
      typeof (value as ArcTargets)[field] === 'number' &&
      Number.isFinite((value as ArcTargets)[field])
  );

/** Fields that differ from a baseline — used to persist coach overrides only. */
export const extractTargetOverrides = (
  targets: ArcTargets,
  baseline: ArcTargets
): Partial<ArcTargets> => {
  const overrides: Partial<ArcTargets> = {};
  for (const field of ARC_TARGET_FIELDS) {
    if (!targetValuesEqual(targets[field], baseline[field])) {
      overrides[field] = targets[field];
    }
  }
  return overrides;
};

export const resolveArcTargets = (
  defaults: ArcTargets,
  overrides: Partial<ArcTargets>
): ArcTargets => ({
  ...defaults,
  ...overrides,
});

/** Legacy sessionStorage wrote the full target object; keep only true coach edits. */
export const migrateLegacyStoredTargets = (stored: ArcTargets): Partial<ArcTargets> =>
  extractTargetOverrides(stored, LEGACY_DEFAULT_TARGETS);

export const parseStoredArcTargets = (raw: string | null): ArcTargets => {
  if (!raw) {
    return { ...DEFAULT_TARGETS };
  }

  try {
    const parsed = JSON.parse(raw) as StoredArcTargetsV2 | ArcTargets;

    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'version' in parsed &&
      'overrides' in parsed &&
      parsed.version === TARGETS_STORAGE_VERSION &&
      typeof parsed.overrides === 'object' &&
      parsed.overrides !== null
    ) {
      return resolveArcTargets(DEFAULT_TARGETS, parsed.overrides);
    }

    if (isArcTargetsShape(parsed)) {
      return resolveArcTargets(DEFAULT_TARGETS, migrateLegacyStoredTargets(parsed));
    }

    return { ...DEFAULT_TARGETS };
  } catch {
    return { ...DEFAULT_TARGETS };
  }
};

export const serializeArcTargets = (targets: ArcTargets): string =>
  JSON.stringify({
    version: TARGETS_STORAGE_VERSION,
    overrides: extractTargetOverrides(targets, DEFAULT_TARGETS),
  } satisfies StoredArcTargetsV2);

export const STATUS_STYLES: Record<MarkerStatus, string> = {
  good: `${ARC_STATUS.good.softBorderClass} ${ARC_STATUS.good.softBgClass} ${ARC_STATUS.good.textClass}`,
  caution: `${ARC_STATUS.caution.softBorderClass} ${ARC_STATUS.caution.softBgClass} ${ARC_STATUS.caution.textClass}`,
  bad: `${ARC_STATUS.bad.softBorderClass} ${ARC_STATUS.bad.softBgClass} ${ARC_STATUS.bad.textClass}`,
  na: `${ARC_STATUS.na.softBorderClass} ${ARC_STATUS.na.softBgClass} ${ARC_STATUS.na.textClass}`,
};

export const METRIC_LABELS: Record<MetricType, string> = {
  catch: 'Catch Angle',
  finish: 'Finish Angle',
  gap: 'Catch Gap',
  slip: 'Finish Slip',
};

export interface TargetHitRate {
  label: string;
  value: number | null;
}

/** Labels used by {@link buildTargetHitRates} (header / session summary). */
export const METRIC_HIT_RATE_LABELS: Record<MetricType, string> = {
  catch: 'Catch',
  finish: 'Finish',
  gap: 'Gap',
  slip: 'Slip',
};

export const getTargetHitRateForMetric = (
  metric: MetricType,
  hitRates: TargetHitRate[]
): number | null => {
  const label = METRIC_HIT_RATE_LABELS[metric];
  return hitRates.find((rate) => rate.label === label)?.value ?? null;
};

export interface SeatMetricHighlight {
  label: string;
  detail: string;
}

export interface SeatMetricSummary {
  label: string;
  median: number | null;
  spread: number | null;
  hitRate: number | null;
  targetLabel: string;
}

const median = (values: number[]): number | null => {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

const spread = (values: number[]): number | null => {
  if (values.length < 2) {
    return null;
  }
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
};

const bandMiss = (value: number, min: number, max: number): number => {
  if (value < min) {
    return min - value;
  }
  if (value > max) {
    return value - max;
  }
  return 0;
};

const penaltyExcess = (value: number, max: number): number => Math.max(0, value - max);

const formatSpread = (value: number | null) => (value == null ? '—' : value.toFixed(1));

const isCatchGapActionable = (stroke: StrokeSample) =>
  stroke.catchSlipApplicable !== false && stroke.catchSlipConfidence !== 'low';

/** Session median ± spread and in-target % per metric for a seat. */
export const buildSeatMetricSummaries = (
  seatStrokes: StrokeSample[],
  targets: ArcTargets,
  hitRates: TargetHitRate[]
): SeatMetricSummary[] => {
  const gapStrokes = seatStrokes.filter((stroke) => isCatchGapActionable(stroke));
  const hitRateFor = (label: string) =>
    hitRates.find((rate) => rate.label === label)?.value ?? null;

  return [
    {
      label: 'Catch',
      median: median(seatStrokes.map((stroke) => stroke.catchAngleDeg)),
      spread: spread(seatStrokes.map((stroke) => stroke.catchAngleDeg)),
      hitRate: hitRateFor('Catch'),
      targetLabel: `${targets.catchAngleMin.toFixed(0)}–${targets.catchAngleMax.toFixed(0)}°`,
    },
    {
      label: 'Finish',
      median: median(seatStrokes.map((stroke) => stroke.finishAngleDeg)),
      spread: spread(seatStrokes.map((stroke) => stroke.finishAngleDeg)),
      hitRate: hitRateFor('Finish'),
      targetLabel: `${targets.finishAngleMin.toFixed(0)}–${targets.finishAngleMax.toFixed(0)}°`,
    },
    {
      label: 'Gap',
      median: median(gapStrokes.map((stroke) => stroke.catchSlipDeg)),
      spread: spread(gapStrokes.map((stroke) => stroke.catchSlipDeg)),
      hitRate: hitRateFor('Gap'),
      targetLabel: `≤${targets.catchGapMax.toFixed(0)}°`,
    },
    {
      label: 'Slip',
      median: median(seatStrokes.map((stroke) => stroke.finishSlipDeg)),
      spread: spread(seatStrokes.map((stroke) => stroke.finishSlipDeg)),
      hitRate: hitRateFor('Slip'),
      targetLabel: `≤${targets.finishSlipMax.toFixed(0)}°`,
    },
  ];
};

export const formatSeatMetricDegrees = (summary: SeatMetricSummary): string => {
  if (summary.median == null) {
    return '—';
  }
  return `${summary.median.toFixed(1)}° ± ${formatSpread(summary.spread)}`;
};

export const formatSeatMetricSummary = (summary: SeatMetricSummary): string => {
  const degrees = formatSeatMetricDegrees(summary);
  if (summary.hitRate == null) {
    return degrees;
  }
  return `${degrees} · ${Math.round(summary.hitRate)}%`;
};

interface MetricCoachScore {
  label: string;
  problemScore: number;
  medianDeficit: number;
  detail: string;
}

/** Minimum in-target % before a metric can appear as "keep up". */
export const KEEP_UP_MIN_HIT_RATE = 70;

/** Below this in-target %, a median-in-target metric can still be "work on". */
export const WORK_ON_MEDIAN_OK_MAX_HIT_RATE = 50;

const getMetricHitRate = (label: string, hitRates: TargetHitRate[]): number | null =>
  hitRates.find((rate) => rate.label === label)?.value ?? null;

const medianDeficitForMetric = (
  label: string,
  seatStrokes: StrokeSample[],
  targets: ArcTargets,
  gapStrokes: StrokeSample[]
): number => {
  switch (label) {
    case 'Catch': {
      const value = median(seatStrokes.map((stroke) => stroke.catchAngleDeg));
      return value == null ? 0 : bandMiss(value, targets.catchAngleMin, targets.catchAngleMax);
    }
    case 'Finish': {
      const value = median(seatStrokes.map((stroke) => stroke.finishAngleDeg));
      return value == null ? 0 : bandMiss(value, targets.finishAngleMin, targets.finishAngleMax);
    }
    case 'Gap': {
      const value = median(gapStrokes.map((stroke) => stroke.catchSlipDeg));
      return value == null ? 0 : penaltyExcess(value, targets.catchGapMax);
    }
    case 'Slip': {
      const value = median(seatStrokes.map((stroke) => stroke.finishSlipDeg));
      return value == null ? 0 : penaltyExcess(value, targets.finishSlipMax);
    }
    default:
      return 0;
  }
};

const formatCoachMetricDetail = (
  label: string,
  degrees: number,
  hitRates: TargetHitRate[]
): string => {
  const hitRate = getMetricHitRate(label, hitRates);
  const degreesLabel = `${degrees.toFixed(1)}°`;
  return hitRate == null ? degreesLabel : `${degreesLabel} · ${Math.round(hitRate)}%`;
};

const buildMetricCoachScores = (
  seatStrokes: StrokeSample[],
  targets: ArcTargets,
  hitRates: TargetHitRate[]
): MetricCoachScore[] => {
  if (seatStrokes.length === 0) {
    return [];
  }

  const gapStrokes = seatStrokes.filter((stroke) => isCatchGapActionable(stroke));

  const catchMisses = seatStrokes.map((stroke) =>
    bandMiss(stroke.catchAngleDeg, targets.catchAngleMin, targets.catchAngleMax)
  );
  const finishMisses = seatStrokes.map((stroke) =>
    bandMiss(stroke.finishAngleDeg, targets.finishAngleMin, targets.finishAngleMax)
  );
  const gapExcess = gapStrokes.map((stroke) =>
    penaltyExcess(stroke.catchSlipDeg, targets.catchGapMax)
  );
  const slipExcess = seatStrokes.map((stroke) =>
    penaltyExcess(stroke.finishSlipDeg, targets.finishSlipMax)
  );

  const peak = (values: number[]) => (values.length === 0 ? 0 : Math.max(...values));

  const catchMedian = median(seatStrokes.map((stroke) => stroke.catchAngleDeg)) ?? 0;
  const finishMedian = median(seatStrokes.map((stroke) => stroke.finishAngleDeg)) ?? 0;
  const gapMedian = median(gapStrokes.map((stroke) => stroke.catchSlipDeg)) ?? 0;
  const slipMedian = median(seatStrokes.map((stroke) => stroke.finishSlipDeg)) ?? 0;

  const scores: MetricCoachScore[] = [
    {
      label: 'Catch',
      problemScore: peak(catchMisses),
      medianDeficit: medianDeficitForMetric('Catch', seatStrokes, targets, gapStrokes),
      detail: formatCoachMetricDetail('Catch', catchMedian, hitRates),
    },
    {
      label: 'Finish',
      problemScore: peak(finishMisses),
      medianDeficit: medianDeficitForMetric('Finish', seatStrokes, targets, gapStrokes),
      detail: formatCoachMetricDetail('Finish', finishMedian, hitRates),
    },
  ];

  if (gapStrokes.length > 0) {
    scores.push({
      label: 'Gap',
      problemScore: peak(gapExcess),
      medianDeficit: medianDeficitForMetric('Gap', seatStrokes, targets, gapStrokes),
      detail: formatCoachMetricDetail('Gap', gapMedian, hitRates),
    });
  }

  scores.push({
    label: 'Slip',
    problemScore: peak(slipExcess),
    medianDeficit: medianDeficitForMetric('Slip', seatStrokes, targets, gapStrokes),
    detail: formatCoachMetricDetail('Slip', slipMedian, hitRates),
  });

  return scores;
};

const isEligibleForWorkOn = (score: MetricCoachScore, hitRates: TargetHitRate[]): boolean => {
  const hitRate = getMetricHitRate(score.label, hitRates);
  if (score.medianDeficit > 0) {
    return true;
  }
  return hitRate != null && hitRate < WORK_ON_MEDIAN_OK_MAX_HIT_RATE;
};

const pickWorkOnMetric = (
  scores: MetricCoachScore[],
  hitRates: TargetHitRate[]
): MetricCoachScore => {
  const eligible = scores.filter((score) => isEligibleForWorkOn(score, hitRates));
  const pool = eligible.length > 0 ? eligible : scores;

  return [...pool].sort((a, b) => {
    const hitA = getMetricHitRate(a.label, hitRates) ?? 101;
    const hitB = getMetricHitRate(b.label, hitRates) ?? 101;
    if (hitA !== hitB) {
      return hitA - hitB;
    }
    if (b.medianDeficit !== a.medianDeficit) {
      return b.medianDeficit - a.medianDeficit;
    }
    return b.problemScore - a.problemScore;
  })[0];
};

const isEligibleForKeepUp = (score: MetricCoachScore, hitRates: TargetHitRate[]): boolean => {
  const hitRate = getMetricHitRate(score.label, hitRates);
  if (hitRate != null && hitRate >= KEEP_UP_MIN_HIT_RATE) {
    return true;
  }
  return score.problemScore <= 0;
};

const pickKeepUpMetric = (
  scores: MetricCoachScore[],
  hitRates: TargetHitRate[]
): MetricCoachScore | null => {
  const eligible = scores.filter((score) => isEligibleForKeepUp(score, hitRates));
  if (eligible.length === 0) {
    return null;
  }

  return [...eligible].sort((a, b) => {
    const hitA = getMetricHitRate(a.label, hitRates) ?? -1;
    const hitB = getMetricHitRate(b.label, hitRates) ?? -1;
    if (hitB !== hitA) {
      return hitB - hitA;
    }
    return a.problemScore - b.problemScore;
  })[0];
};

/** Coach-facing keep-up / work-on lines from session hit rate and median deviation. */
export const buildSeatCoachHighlights = (
  seatStrokes: StrokeSample[],
  targets: ArcTargets,
  hitRates: TargetHitRate[]
): { strongest: SeatMetricHighlight | null; weakest: SeatMetricHighlight } | null => {
  const scores = buildMetricCoachScores(seatStrokes, targets, hitRates);
  if (scores.length < 2) {
    return null;
  }

  const workOn = pickWorkOnMetric(scores, hitRates);
  const keepUp = pickKeepUpMetric(scores, hitRates);

  if (workOn.problemScore <= 0 && workOn.medianDeficit <= 0) {
    const praise = keepUp ?? workOn;
    return {
      strongest: { label: praise.label, detail: praise.detail },
      weakest: { label: praise.label, detail: praise.detail },
    };
  }

  return {
    strongest: keepUp ? { label: keepUp.label, detail: keepUp.detail } : null,
    weakest: { label: workOn.label, detail: workOn.detail },
  };
};

/** @deprecated Use {@link buildSeatCoachHighlights} */
export const buildSeatMetricHighlights = (
  hitRates: TargetHitRate[]
): { strongest: SeatMetricHighlight; weakest: SeatMetricHighlight } | null => {
  const measurable = hitRates.flatMap((rate) =>
    rate.value == null ? [] : [{ label: rate.label, value: rate.value }]
  );
  if (measurable.length < 2) {
    return null;
  }
  const sorted = [...measurable].sort((a, b) => b.value - a.value);
  return {
    strongest: { label: sorted[0].label, detail: `${Math.round(sorted[0].value)}%` },
    weakest: {
      label: sorted[sorted.length - 1].label,
      detail: `${Math.round(sorted[sorted.length - 1].value)}%`,
    },
  };
};

export const CREW_SUMMARY_METRIC_ORDER = ['Catch', 'Finish', 'Gap', 'Slip'] as const;

export const CREW_SUMMARY_LABEL_TO_METRIC: Record<
  (typeof CREW_SUMMARY_METRIC_ORDER)[number],
  MetricType
> = {
  Catch: 'catch',
  Finish: 'finish',
  Gap: 'gap',
  Slip: 'slip',
};

export interface CrewSeatSummary {
  seat: number;
  strokeCount: number;
  hitRates: TargetHitRate[];
  metricSummaries: SeatMetricSummary[];
  highlights: { strongest: SeatMetricHighlight | null; weakest: SeatMetricHighlight } | null;
}

export interface CrewMetricRanks {
  byMetric: Record<string, Map<number, number>>;
  crewSize: number;
}

/** Session in-target % per seat for the crew summary modal. */
export const buildCrewArcSummary = (
  strokes: StrokeSample[],
  targets: ArcTargets,
  seats: number[]
): CrewSeatSummary[] =>
  seats.map((seat) => {
    const seatStrokes = getSeatStrokes(strokes, seat);
    const hitRates = buildTargetHitRates(seatStrokes, targets);
    return {
      seat,
      strokeCount: seatStrokes.length,
      hitRates,
      metricSummaries: buildSeatMetricSummaries(seatStrokes, targets, hitRates),
      highlights: buildSeatCoachHighlights(seatStrokes, targets, hitRates),
    };
  });

/** Boat rank per metric (1 = highest in-target %). Tied values share the same rank. */
export const buildCrewMetricRanks = (summaries: CrewSeatSummary[]): CrewMetricRanks => {
  const crewSize = summaries.filter((summary) => summary.strokeCount > 0).length;
  const byMetric: Record<string, Map<number, number>> = {};

  for (const label of CREW_SUMMARY_METRIC_ORDER) {
    const ranked = summaries.flatMap((summary) => {
      const value = summary.hitRates.find((rate) => rate.label === label)?.value;
      return value == null ? [] : [{ seat: summary.seat, value }];
    });
    ranked.sort((a, b) => b.value - a.value);

    const ranks = new Map<number, number>();
    ranked.forEach((item, index) => {
      const prev = index > 0 ? ranked[index - 1] : null;
      const rank = prev && prev.value === item.value ? ranks.get(prev.seat)! : index + 1;
      ranks.set(item.seat, rank);
    });
    byMetric[label] = ranks;
  }

  return { byMetric, crewSize };
};

export const getHitRatePercentStatus = (
  value: number | null
): 'good' | 'caution' | 'bad' | 'na' => {
  if (value == null) {
    return 'na';
  }
  if (value >= 75) {
    return 'good';
  }
  if (value >= 50) {
    return 'caution';
  }
  return 'bad';
};

export const formatArcTargetsSummary = (targets: ArcTargets): string =>
  `Catch ${targets.catchAngleMin.toFixed(0)}–${targets.catchAngleMax.toFixed(0)}° · Finish ${targets.finishAngleMin.toFixed(0)}–${targets.finishAngleMax.toFixed(0)}° · Gap ≤${targets.catchGapMax.toFixed(0)}° · Slip ≤${targets.finishSlipMax.toFixed(0)}°`;

export const formatCoachHighlightLine = (highlight: SeatMetricHighlight): string =>
  `${highlight.label} · ${highlight.detail}`;

export const getSeatStrokes = (strokes: StrokeSample[], selectedSeat: number) =>
  strokes
    .filter((stroke) => stroke.rowerPosition === selectedSeat)
    .sort((a, b) => a.strokeIndex - b.strokeIndex);

export const getStrokeMetricStatus = (
  metric: MetricType,
  stroke: StrokeSample,
  targets: ArcTargets
): MarkerStatus => {
  if (metric === 'gap' && !isCatchGapActionable(stroke)) {
    return 'na';
  }
  const details = getMetricDetails(metric, stroke, targets);
  return calculateStatus(details.value, details.min, details.max, targets.warningBand);
};

export const calculateStatus = (
  value: number,
  min?: number,
  max?: number,
  warningBand: number = 0.05
): MarkerStatus => {
  if (min !== undefined && max !== undefined) {
    const range = max - min;
    const warningMin = min + range * warningBand;
    const warningMax = max - range * warningBand;

    if (value < min || value > max) {
      return 'bad';
    }
    if (value >= warningMin && value <= warningMax) {
      return 'good';
    }
    return 'caution';
  }

  if (max !== undefined) {
    const warningThreshold = max * (1 - warningBand);

    if (value > max) {
      return 'bad';
    }
    if (value <= warningThreshold) {
      return 'good';
    }
    return 'caution';
  }

  if (min !== undefined) {
    const warningThreshold = min * (1 + warningBand);

    if (value < min) {
      return 'bad';
    }
    if (value >= warningThreshold) {
      return 'good';
    }
    return 'caution';
  }

  return 'good';
};

export const getMetricValue = (metric: MetricType, stroke: StrokeSample): number => {
  switch (metric) {
    case 'catch':
      return stroke.catchAngleDeg;
    case 'finish':
      return stroke.finishAngleDeg;
    case 'gap':
      return stroke.catchSlipDeg;
    case 'slip':
      return stroke.finishSlipDeg;
    default:
      return 0;
  }
};

export const getMetricDetails = (
  metric: MetricType,
  stroke: StrokeSample,
  targets: ArcTargets
): MetricDetails => {
  const targetRange = getMetricTargetRange(metric, targets);

  switch (metric) {
    case 'catch':
      return {
        label: METRIC_LABELS.catch,
        value: stroke.catchAngleDeg,
        target: targetRange.label,
        min: targetRange.min,
        max: targetRange.max,
      };
    case 'finish':
      return {
        label: METRIC_LABELS.finish,
        value: stroke.finishAngleDeg,
        target: targetRange.label,
        min: targetRange.min,
        max: targetRange.max,
      };
    case 'gap':
      return {
        label: METRIC_LABELS.gap,
        value: stroke.catchSlipDeg,
        target: targetRange.label,
        max: targetRange.max,
      };
    case 'slip':
      return {
        label: METRIC_LABELS.slip,
        value: stroke.finishSlipDeg,
        target: targetRange.label,
        max: targetRange.max,
      };
    default:
      return {
        label: '',
        value: 0,
        target: '',
      };
  }
};

export const getMetricTargetRange = (
  metric: MetricType,
  targets: ArcTargets
): MetricTargetRange => {
  switch (metric) {
    case 'catch':
      return {
        min: targets.catchAngleMin,
        max: targets.catchAngleMax,
        label: `${targets.catchAngleMin.toFixed(1)}° to ${targets.catchAngleMax.toFixed(1)}°`,
      };
    case 'finish':
      return {
        min: targets.finishAngleMin,
        max: targets.finishAngleMax,
        label: `${targets.finishAngleMin.toFixed(1)}° to ${targets.finishAngleMax.toFixed(1)}°`,
      };
    case 'gap':
      return {
        max: targets.catchGapMax,
        label: `Max ${targets.catchGapMax.toFixed(1)}°`,
      };
    case 'slip':
      return {
        max: targets.finishSlipMax,
        label: `Max ${targets.finishSlipMax.toFixed(1)}°`,
      };
    default:
      return {
        label: '',
      };
  }
};

export const buildStrokeStatuses = (
  seatStrokes: StrokeSample[],
  strokeCount: number,
  selectedMetric: MetricType,
  targets: ArcTargets
): MarkerStatus[] => {
  const metricTarget = getMetricTargetRange(selectedMetric, targets);

  return Array.from({ length: strokeCount }, (_, index) => {
    const stroke = seatStrokes[index];
    if (!stroke) {
      return 'good';
    }

    if (selectedMetric === 'gap' && !isCatchGapActionable(stroke)) {
      return 'na';
    }

    const value = getMetricValue(selectedMetric, stroke);

    return calculateStatus(value, metricTarget.min, metricTarget.max, targets.warningBand);
  });
};

export const buildTargetValidationMessages = (targets: ArcTargets): string[] => {
  const messages: string[] = [];

  if (targets.catchAngleMin >= targets.catchAngleMax) {
    messages.push('Catch angle minimum must remain below catch angle maximum.');
  }

  if (targets.finishAngleMin >= targets.finishAngleMax) {
    messages.push('Finish angle minimum must remain below finish angle maximum.');
  }

  if (targets.finishSlipMin > 0 && targets.finishSlipMin >= targets.finishSlipMax) {
    messages.push('Finish slip minimum must remain below finish slip maximum.');
  }

  return messages;
};

export const buildTargetHitRates = (seatStrokes: StrokeSample[], targets: ArcTargets) => {
  if (seatStrokes.length === 0) {
    return [
      { label: 'Catch', value: null },
      { label: 'Finish', value: null },
      { label: 'Gap', value: null },
      { label: 'Slip', value: null },
    ];
  }

  const countInTarget = (matcher: (stroke: StrokeSample) => boolean) =>
    (seatStrokes.filter(matcher).length / seatStrokes.length) * 100;

  return [
    {
      label: 'Catch',
      value: countInTarget(
        (stroke) =>
          stroke.catchAngleDeg >= targets.catchAngleMin &&
          stroke.catchAngleDeg <= targets.catchAngleMax
      ),
    },
    {
      label: 'Finish',
      value: countInTarget(
        (stroke) =>
          stroke.finishAngleDeg >= targets.finishAngleMin &&
          stroke.finishAngleDeg <= targets.finishAngleMax
      ),
    },
    {
      label: 'Gap',
      value: (() => {
        const gapApplicable = seatStrokes.filter((s) => isCatchGapActionable(s));
        if (gapApplicable.length === 0) {
          return null;
        }
        return (
          (gapApplicable.filter((stroke) => stroke.catchSlipDeg <= targets.catchGapMax).length /
            gapApplicable.length) *
          100
        );
      })(),
    },
    {
      label: 'Slip',
      value: countInTarget((stroke) => stroke.finishSlipDeg <= targets.finishSlipMax),
    },
  ];
};

export const buildAverageStroke = (
  seatStrokes: StrokeSample[],
  selectedSeat: number
): StrokeSample | null => {
  if (seatStrokes.length === 0) {
    return null;
  }

  const gapApplicable = seatStrokes.filter((s) => isCatchGapActionable(s));

  const total = seatStrokes.reduce(
    (acc, stroke) => ({
      catchAngleDeg: acc.catchAngleDeg + stroke.catchAngleDeg,
      finishAngleDeg: acc.finishAngleDeg + stroke.finishAngleDeg,
      finishSlipDeg: acc.finishSlipDeg + stroke.finishSlipDeg,
    }),
    {
      catchAngleDeg: 0,
      finishAngleDeg: 0,
      finishSlipDeg: 0,
    }
  );

  const catchSlipAvg =
    gapApplicable.length === 0
      ? 0
      : gapApplicable.reduce((sum, s) => sum + s.catchSlipDeg, 0) / gapApplicable.length;

  return {
    strokeId: `seat-${selectedSeat}-average`,
    label: `Seat ${selectedSeat} average`,
    catchAngleDeg: total.catchAngleDeg / seatStrokes.length,
    finishAngleDeg: total.finishAngleDeg / seatStrokes.length,
    catchSlipApplicable: gapApplicable.length > 0,
    catchSlipConfidence: gapApplicable.length > 0 ? 'high' : 'low',
    catchSlipDeg: catchSlipAvg,
    finishSlipDeg: total.finishSlipDeg / seatStrokes.length,
    driveStartMs: null,
    driveTimeMs: null,
    recoveryTimeMs: null,
    dataSource: 'computed_average',
    confidence: null,
    strokeIndex: 0,
    rowerPosition: selectedSeat,
  };
};

export const buildSessionAverages = (seatStrokes: StrokeSample[]): SessionAverages => {
  if (seatStrokes.length === 0) {
    return {
      catchGap: null,
      effectiveLength: null,
      finishSlip: null,
      measuredCatchGapCount: 0,
      totalStrokeCount: 0,
    };
  }

  const gapApplicable = seatStrokes.filter((s) => isCatchGapActionable(s));
  const average = (values: number[]) =>
    values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0) / values.length;

  return {
    catchGap: average(gapApplicable.map((stroke) => stroke.catchSlipDeg)),
    effectiveLength: average(
      seatStrokes.map(
        (stroke) =>
          stroke.catchAngleDeg + stroke.finishAngleDeg - stroke.catchSlipDeg - stroke.finishSlipDeg
      )
    ),
    finishSlip: average(seatStrokes.map((stroke) => stroke.finishSlipDeg)),
    measuredCatchGapCount: gapApplicable.length,
    totalStrokeCount: seatStrokes.length,
  };
};

export const normalizeTargetUpdate = (
  previous: ArcTargets,
  field: keyof ArcTargets,
  value: number
): ArcTargets => {
  const next = { ...previous, [field]: value };

  if (field === 'catchAngleMin') {
    next.catchAngleMin = Math.min(value, previous.catchAngleMax - 0.1);
  }
  if (field === 'catchAngleMax') {
    next.catchAngleMax = Math.max(value, previous.catchAngleMin + 0.1);
  }
  if (field === 'finishAngleMin') {
    next.finishAngleMin = Math.min(value, previous.finishAngleMax - 0.1);
  }
  if (field === 'finishAngleMax') {
    next.finishAngleMax = Math.max(value, previous.finishAngleMin + 0.1);
  }
  if (field === 'finishSlipMin') {
    next.finishSlipMin = Math.min(value, previous.finishSlipMax - 0.1);
  }
  if (field === 'finishSlipMax') {
    next.finishSlipMax = Math.max(value, previous.finishSlipMin + 0.1);
  }
  if (field === 'catchGapMax') {
    next.catchGapMax = Math.max(0, value);
  }

  return next;
};

export const buildMetricMessage = (
  currentMetricDetails: MetricDetails | null,
  currentMetricStatus: MarkerStatus,
  currentStrokeData?: StrokeSample
) => {
  if (!currentMetricDetails || !currentStrokeData) {
    return {
      title: 'No stroke data for this seat yet.',
      detail: 'Select a stroke in the timeline once data is available.',
    };
  }

  const { value, min, max, label, target } = currentMetricDetails;

  if (currentMetricStatus === 'na') {
    if (currentStrokeData.catchSlipApplicable === false) {
      return {
        title: `${label} is not available on stroke ${currentStrokeData.strokeIndex}.`,
        detail:
          'This stroke window does not include recovery before the drive, so catch gap cannot be measured.',
      };
    }
    return {
      title: `${label} is not available on stroke ${currentStrokeData.strokeIndex}.`,
      detail: 'This stroke is marked low confidence for catch gap due to outlier magnitude.',
    };
  }

  if (currentMetricStatus === 'good') {
    return {
      title: `${label} is in target on stroke ${currentStrokeData.strokeIndex}.`,
      detail: `${value.toFixed(1)}° is comfortably inside ${target}.`,
    };
  }

  if (currentMetricStatus === 'caution') {
    return {
      title: `${label} is near the edge on stroke ${currentStrokeData.strokeIndex}.`,
      detail: `${value.toFixed(1)}° is still inside ${target}, but it is close to drifting out.`,
    };
  }

  if (min !== undefined && value < min) {
    return {
      title: `${label} is below target on stroke ${currentStrokeData.strokeIndex}.`,
      detail: `${value.toFixed(1)}° is ${(min - value).toFixed(1)}° below the target floor of ${min.toFixed(1)}°.`,
    };
  }

  if (max !== undefined && value > max) {
    return {
      title: `${label} is above target on stroke ${currentStrokeData.strokeIndex}.`,
      detail: `${value.toFixed(1)}° is ${(value - max).toFixed(1)}° above the target ceiling of ${max.toFixed(1)}°.`,
    };
  }

  return {
    title: `${label} is out of target on stroke ${currentStrokeData.strokeIndex}.`,
    detail: `${value.toFixed(1)}° is outside the target range of ${target}.`,
  };
};

const isFlaggedStatus = (status: MarkerStatus) => status !== 'good' && status !== 'na';

export const findNextFlaggedStrokeNumber = (
  currentStrokeIndex: number,
  strokeStatuses: MarkerStatus[]
) => {
  for (let i = currentStrokeIndex + 1; i < strokeStatuses.length; i += 1) {
    if (isFlaggedStatus(strokeStatuses[i])) {
      return i + 1;
    }
  }

  return null;
};

export const findPrevFlaggedStrokeNumber = (
  currentStrokeIndex: number,
  strokeStatuses: MarkerStatus[]
) => {
  for (let i = currentStrokeIndex - 1; i >= 0; i -= 1) {
    if (isFlaggedStatus(strokeStatuses[i])) {
      return i + 1;
    }
  }

  return null;
};

export const buildStrokeTitles = (
  seatStrokes: StrokeSample[],
  selectedMetric: MetricType,
  strokeCount: number
) =>
  Array.from({ length: strokeCount }, (_, index) => {
    const stroke = seatStrokes[index];
    if (!stroke) {
      return `Stroke ${index + 1}: No data`;
    }

    if (selectedMetric === 'gap' && !isCatchGapActionable(stroke)) {
      if (stroke.catchSlipApplicable === false) {
        return `Stroke ${stroke.strokeIndex}: ${METRIC_LABELS[selectedMetric]} not available (no pre-catch window)`;
      }
      return `Stroke ${stroke.strokeIndex}: ${METRIC_LABELS[selectedMetric]} low confidence (outlier)`;
    }
    const value = getMetricValue(selectedMetric, stroke);
    return `Stroke ${stroke.strokeIndex}: ${METRIC_LABELS[selectedMetric]} ${value.toFixed(1)}°`;
  });

export const formatPlaybackTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const deriveEffectiveLengthTargets = (targets: ArcTargets) => ({
  min: targets.catchAngleMin + targets.finishAngleMin - targets.catchGapMax - targets.finishSlipMax,
  max: targets.catchAngleMax + targets.finishAngleMax - targets.finishSlipMin,
});
